import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Trash2, Edit, Plus, MapPin } from 'lucide-react';

const RoomsTab = ({
  rooms,
  roomForm,
  setRoomForm,
  roomModalOpen,
  setRoomModalOpen,
  editingItem,
  setEditingItem,
  handleRoomSubmit,
  handleDeleteRoom,
  editRoom
}) => {
  const openNewRoom = () => {
    setEditingItem(null);
    setRoomForm({ name: '', capacity: 20, description: '' });
    setRoomModalOpen(true);
  };

  return (
    <div data-testid="rooms-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl font-semibold text-white">Rooms</h2>
        <Button 
          onClick={openNewRoom}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-room-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Room
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {rooms.map(room => (
          <Card key={room.id} className="bg-[#111] border-[#333]">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{room.name}</h3>
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                      {room.capacity} capacity
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => editRoom(room)}
                    className="text-white/50 hover:text-white h-8 w-8"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleDeleteRoom(room.id)}
                    className="text-red-400/50 hover:text-red-400 h-8 w-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {room.description && (
                <p className="text-sm text-white/50 mt-2">{room.description}</p>
              )}
            </CardContent>
          </Card>
        ))}
        {rooms.length === 0 && (
          <Card className="bg-[#111] border-[#333] col-span-full">
            <CardContent className="py-12 text-center text-white/50">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p>No rooms yet. Create your first room!</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Room Modal */}
      <Dialog open={roomModalOpen} onOpenChange={setRoomModalOpen}>
        <DialogContent className="bg-[#111] border-[#333]">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingItem ? 'Edit Room' : 'Add New Room'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">Name *</Label>
              <Input 
                value={roomForm.name} 
                onChange={e => setRoomForm({...roomForm, name: e.target.value})} 
                placeholder="Studio A" 
                className="bg-[#0a0a0a] border-[#333] text-white"
                data-testid="room-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Capacity *</Label>
              <Input 
                type="number" 
                value={roomForm.capacity} 
                onChange={e => setRoomForm({...roomForm, capacity: parseInt(e.target.value)})}
                className="bg-[#0a0a0a] border-[#333] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Description</Label>
              <Textarea 
                value={roomForm.description} 
                onChange={e => setRoomForm({...roomForm, description: e.target.value})}
                placeholder="Room description..."
                className="bg-[#0a0a0a] border-[#333] text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoomModalOpen(false)} className="border-[#333] text-white/70">
              Cancel
            </Button>
            <Button onClick={handleRoomSubmit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-room-btn">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomsTab;
